/**
 * Pure helpers for the shared SmartAI library view of the gallery.
 */
import type { LibraryItem, LibraryKind, LibraryProduct } from '../../domain';
import { formatDimensions } from './assetUtils';

/** Which products' work the gallery shows. 'coral' is the existing gallery. */
export type LibrarySource = 'coral' | 'lattice' | 'all';

export const PRODUCT_LABEL: Readonly<Record<LibraryProduct, string>> = {
  coral: 'Coral Creator',
  lattice: 'Lattice',
};

const KIND_LABEL: Readonly<Record<LibraryKind, string>> = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  model3d: '3D model',
  vector: 'Vector',
  document: 'Document',
  other: 'File',
};

export const kindLabel = (kind: LibraryKind): string => KIND_LABEL[kind] ?? 'File';

/** Only images and video can be imported into Coral (the API answers 415 otherwise). */
export const isImportableKind = (kind: LibraryKind): boolean =>
  kind === 'image' || kind === 'video';

/** "0:07" / "1:02:03" from milliseconds; '' when unknown. */
export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** The card's meta line: kind · dimensions · duration. */
export function libraryItemMeta(item: LibraryItem): string {
  return [
    kindLabel(item.kind),
    formatDimensions(item.width ?? undefined, item.height ?? undefined),
    formatDuration(item.durationMs),
  ]
    .filter(Boolean)
    .join(' · ');
}

const EXT_BY_MIME: Readonly<Record<string, string>> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/ogg': 'ogg',
  'model/gltf-binary': 'glb',
  'application/pdf': 'pdf',
};

/** A download filename like "lattice-<id>.png". */
export function libraryFilename(item: LibraryItem): string {
  const mime = item.mimeType?.split(';')[0].trim().toLowerCase() ?? '';
  const ext = EXT_BY_MIME[mime] ?? mime.split('/')[1]?.replace(/[^a-z0-9]/g, '');
  return `${item.product}-${item.id}${ext ? `.${ext}` : ''}`;
}

/** Newest first across products; stable for equal timestamps. */
export function mergeLibraryItems(...lists: LibraryItem[][]): LibraryItem[] {
  const time = (i: LibraryItem) => {
    const t = Date.parse(i.createdAt);
    return Number.isNaN(t) ? 0 : t;
  };
  return lists.flat().sort((a, b) => time(b) - time(a));
}
